import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useMemo } from 'react';
import { merge } from 'ts-deepmerge';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import type { GigawidgetSettings } from '../../universal-widget/util';
import {
  useDashboardFilters,
  useMyItemsFilters,
} from '../../useDashboardStore';
import type {
  DataSourceVariables,
  DateFilterOptions,
  WidgetDataSource,
} from '../types';

const applyFilter = <T, K>(filterFunc?: (value: T) => K, value?: T) => {
  if (Array.isArray(value) && value.length === 0) {
    return {};
  }
  if (value == null) {
    return {};
  }
  if (!filterFunc) {
    return {};
  }

  return filterFunc(value);
};

/**
 * The dashboard date range mapped through a `dateFilter` callback, WITHOUT the
 * `ignoreDashboardDateFilter` gate `useDataSourceFilter` applies.
 *
 * The two seams answer different questions. `ignoreDashboardDateFilter` means
 * "keep the dashboard's date range off my ROWS" — which all six over-time
 * charts set, since narrowing the rows would drop whole lines from the chart
 * rather than shortening them. Clipping their NESTED result series is a
 * separate concern that must apply either way: pre-COI-600 those widgets
 * expressed it as a raw `variables` block (`resultsWhere`,
 * `riskAssessmentResultsWhere`, …) that never passed through this hook, so the
 * gate never touched it.
 *
 * COI-600 routed the clip through `dateFilterOptions` and so put it behind the
 * gate, silently disabling it — a date-filtered dashboard on the coi-600
 * ephemeral env sent `getIndicators { resultsWhere: {} }`. Keep the seams
 * separate; do not fold this back into `useDataSourceFilter`.
 */
export const useSeriesDateFilter = <T extends WidgetDataSource>(
  options?: DateFilterOptions<T>
) => {
  const filters = useDashboardFilters();
  // Keyed on the callback and precision VALUES rather than the options object:
  // `UniversalChart` builds a fresh `dateFilterOptions` literal every render,
  // but the fields themselves come from module-level data-source definitions and
  // are stable.
  const dateFilter = options?.dateFilter;
  const precision = options?.precision;

  return useMemo(
    () =>
      applyFilter(
        (dateRange) => dateFilter?.(dateRange, precision ?? 'day') ?? {},
        filters.dateRange
      ) as Partial<DataSourceVariables<T>>,
    [dateFilter, precision, filters.dateRange]
  );
};

export const useDataSourceFilter = <T extends WidgetDataSource>(
  dataSource: T,
  dateFilterOptions?: DateFilterOptions<T>
) => {
  const filters = useDashboardFilters();
  const myItemsFilters = useMyItemsFilters();
  const [settings] = useDashboardWidgetSettings<GigawidgetSettings>();
  const { user } = useRisksmartUser();

  const { departments, tags, dateRange } = filters;
  const userId = user?.userId;
  const dateFilterCallback = dateFilterOptions?.dateFilter;
  const precision = dateFilterOptions?.precision;

  return useMemo(() => {
    const departmentFilter = applyFilter(
      dataSource.dashboardFilterConfig.departmentsFilter,
      departments
    );
    const tagFilter = applyFilter(
      dataSource.dashboardFilterConfig.tagsFilter,
      tags
    );
    const dateFilter = applyFilter(
      (value) => dateFilterCallback?.(value, precision ?? 'day') ?? {},
      dateRange
    );

    const ownershipFilter = applyFilter(
      (ownershipFilters) =>
        dataSource.dashboardFilterConfig.ownershipFilter?.(
          ownershipFilters,
          userId
        ) ?? {},
      myItemsFilters
    );

    if (settings?.allowOwnershipFiltering) {
      return ownershipFilter as DataSourceVariables<T>;
    }

    return merge(
      departmentFilter,
      tagFilter,
      settings?.ignoreDashboardDateFilter ? {} : dateFilter
    ) as DataSourceVariables<T>;
  }, [
    dataSource,
    departments,
    tags,
    dateRange,
    myItemsFilters,
    settings,
    userId,
    dateFilterCallback,
    precision,
  ]);
};
